// src/pages/dashboard/alumnos/components/EditarFamiliaModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Card, Row, Col, message, Spin } from 'antd';
import { UserOutlined, TeamOutlined } from '@ant-design/icons';
import apiClient from '../../../../api/apiClient';
import EditarResponsablesModal from './EditarResponsablesModal';

const EditarFamiliaModal = ({ open, onCancel, familiaData, onFamiliaActualizada }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showResponsablesModal, setShowResponsablesModal] = useState(false);
  const [showUsuario, setShowUsuario] = useState(false);
  const [usuarioFamilia, setUsuarioFamilia] = useState(null);
  const [loadingUsuario, setLoadingUsuario] = useState(false);

  // Resetear estados cuando cambia la familia o se abre/cierra el modal
  useEffect(() => {
    setUsuarioFamilia(null);
    setShowUsuario(false);
    setShowResponsablesModal(false);
  }, [familiaData, open]);

  // Prellenar el formulario con datos de familia
  useEffect(() => {
    if (familiaData && open) {
      form.setFieldsValue({
        NombreFamilia: familiaData.NombreFamilia || '',
        NombreRecibo: familiaData.NombreRecibo || '',
        TelefonoRecibo: familiaData.TelefonoRecibo || '',
        CorreoElectronico: familiaData.CorreoElectronico || '',
        DireccionRecibo: familiaData.DireccionRecibo || '',
      });
    }
  }, [familiaData, open, form]);

  // Cargar usuario de la familia cuando se muestra la sección
  const cargarUsuarioFamilia = async () => {
    if (!familiaData?.IdUsuario) {
      setUsuarioFamilia(null);
      return;
    }
    setLoadingUsuario(true);
    try {
      const res = await apiClient.get(`/usuarios/${familiaData.IdUsuario}`);
      const datos = res.data.data || res.data;
      setUsuarioFamilia(datos);
      form.setFieldsValue({
        UsuarioNombreUsuario: datos.NombreUsuario || '',
        UsuarioNombreCompleto: datos.NombreCompleto || '',
      });
    } catch (error) {
      message.error('Error al cargar datos del usuario');
      setUsuarioFamilia(null);
    } finally {
      setLoadingUsuario(false);
    }
  };

  const handleToggleUsuario = () => {
    if (!showUsuario && !usuarioFamilia) {
      cargarUsuarioFamilia();
    }
    setShowUsuario(!showUsuario);
  };

  // Guardar cambios del usuario
  const handleGuardarUsuario = async () => {
    try {
      const values = await form.validateFields(['UsuarioNombreUsuario', 'UsuarioNombreCompleto']);

      const userFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
      const ModificadoPor = userFromStorage.IdUsuario;

      if (!ModificadoPor) {
        message.error('No se encontró el usuario en sesión.');
        return;
      }

      setLoadingUsuario(true);

      await apiClient.put(`/usuarios/update-profile/${familiaData.IdUsuario}`, {
        NombreUsuario: values.UsuarioNombreUsuario,
        NombreCompleto: values.UsuarioNombreCompleto,
        ModificadoPor,
      });

      setUsuarioFamilia(prev => ({
        ...prev,
        NombreUsuario: values.UsuarioNombreUsuario,
        NombreCompleto: values.UsuarioNombreCompleto,
      }));

      message.success('Usuario actualizado correctamente');
    } catch (error) {
      const mensajeError = error.response?.data?.error || error.response?.data?.message || 'Error al actualizar usuario';
      message.error(mensajeError);
    } finally {
      setLoadingUsuario(false);
    }
  };

  const handleGuardar = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const userFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
      const IdColaborador = userFromStorage.IdUsuario;

      if (!IdColaborador) {
        message.error('No se encontró el usuario en sesión. Por favor, recarga la página.');
        setLoading(false);
        return;
      }

      // Actualizar datos de familia
      await apiClient.put(`/familias/${familiaData.IdFamilia}`, {
        NombreFamilia: values.NombreFamilia,
        NombreRecibo: values.NombreRecibo,
        TelefonoContacto: values.TelefonoRecibo,
        EmailContacto: values.CorreoElectronico,
        DireccionRecibo: values.DireccionRecibo,
        IdColaborador,
      });

      message.success('Datos actualizados correctamente');

      const familiaActualizada = {
        ...familiaData,
        NombreFamilia: values.NombreFamilia,
        NombreRecibo: values.NombreRecibo,
        TelefonoRecibo: values.TelefonoRecibo,
        CorreoElectronico: values.CorreoElectronico,
        DireccionRecibo: values.DireccionRecibo,
      };
      onFamiliaActualizada(familiaActualizada);
      onCancel();
    } catch (error) {
      message.error('Error al guardar los cambios');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Editar Datos de Facturación y Familia"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={1000}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Form form={form} layout="vertical">
          <Card title="Datos de Familia" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Nombre de la Familia" name="NombreFamilia" rules={[{ required: true, message: 'El nombre de la familia es requerido' }]}>
                  <Input placeholder="Ej: Pérez López" style={{ fontSize: 16, fontWeight: 'bold' }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card title="Datos de Facturación" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Nombre para recibo" name="NombreRecibo">
                  <Input placeholder="Ej: Familia Pérez López" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Teléfono de contacto" name="TelefonoRecibo">
                  <Input placeholder="4444-5555" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Email" name="CorreoElectronico">
                  <Input placeholder="familia@email.com" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Dirección completa" name="DireccionRecibo">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <div style={{ textAlign: 'center', margin: '30px 0', display: 'flex', justifyContent: 'center', gap: 16 }}>
            <Button
              type="dashed"
              size="large"
              icon={<TeamOutlined />}
              onClick={() => setShowResponsablesModal(true)}
              style={{ width: 250 }}
            >
              Editar Responsables
            </Button>
            <Button
              type="dashed"
              size="large"
              icon={<UserOutlined />}
              onClick={handleToggleUsuario}
              style={{ width: 250 }}
              disabled={!familiaData?.IdUsuario}
            >
              {showUsuario ? 'Ocultar Usuario' : 'Editar Usuario'}
            </Button>
          </div>

          {showUsuario && (
            <Card
              title="Usuario de la Familia"
              style={{ marginTop: 16, marginBottom: 16 }}
              extra={
                usuarioFamilia && (
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleGuardarUsuario}
                    loading={loadingUsuario}
                  >
                    Guardar Usuario
                  </Button>
                )
              }
            >
              {loadingUsuario && !usuarioFamilia ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <Spin />
                </div>
              ) : usuarioFamilia ? (
                <div style={{ padding: 16, background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Nombre de Usuario"
                        name="UsuarioNombreUsuario"
                        rules={[{ required: true, message: 'El nombre de usuario es requerido' }]}
                      >
                        <Input placeholder="Nombre de usuario" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Nombre Completo"
                        name="UsuarioNombreCompleto"
                        rules={[{ required: true, message: 'El nombre completo es requerido' }]}
                      >
                        <Input placeholder="Nombre completo" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: 16 }}>
                  No se encontró usuario asociado a esta familia
                </div>
              )}
            </Card>
          )}

          <div style={{ textAlign: 'right', marginTop: 32 }}>
            <Button onClick={onCancel} style={{ marginRight: 12 }}>
              Cancelar
            </Button>
            <Button type="primary" size="large" onClick={handleGuardar} loading={loading}>
              Guardar Todos los Cambios
            </Button>
          </div>
        </Form>
      )}

      {/* Modal de Editar Responsables */}
      <EditarResponsablesModal
        open={showResponsablesModal}
        onCancel={() => setShowResponsablesModal(false)}
        idFamilia={familiaData?.IdFamilia}
      />
    </Modal>
  );
};

export default EditarFamiliaModal;
